import { CardData } from "./CardServices";
import { rarityEnum } from "../enums/rarityEnum";
import api from "./BaseServices";
import { reportStatusEnum } from "../enums/reportStatusEnum";

// Define the booster interface for TypeScript
export interface boosterData {
  id: string;
  image_id: string;
  name: string;
  description: string;
  masterset_card_id: number;
  is_in_store: boolean;
  price: number;
  original_image_id: string;
  created_at: string;
  updated_at: string;
  username?: string;
  profile_picture_url?: string;
  quantity_sold?: number;
  status: reportStatusEnum;
}

export interface GenerateBoosterData extends boosterData {
  offset_y: number;
  offset_x: number;
  width: number;
  zoom: number;
  image_file?: Blob | null;
  border_color: string;
  username: string;
}

export interface BoosterCards {
  id: number;
  name: string;
  image_id: string;
  blurred_image_id: string;
  small_image_id: string;
  is_owned: boolean;
  blur_level: string;
  rarity: rarityEnum;
  updated_at: Date;
}

export interface deleteBoostersResult {
  deletedIds: string[];
  errors: {
    storeBoosters: string[];
    collectorBoosters: string[];
  };
}

// API base URL (replace with your backend API base URL)
const API_URL = `${process.env.REACT_APP_API_URL}/boosters`; // Modify this if your API is on a different URL

// Function to fetch all boosters with pagination (limit and offset)
export const getCreatorBoosters = async (
  limit: number,
  offset: number
): Promise<boosterData[] & { quantity_sold?: number }> => {
  const response = await api.get(API_URL, {
    params: {
      limit,
      offset,
    },
  });
  return response.data;
};

export const getUserBoosters = async (
  limit: number,
  offset: number,
  userId: number
): Promise<boosterData[]> => {
  const response = await api.get(
    `${process.env.REACT_APP_API_URL}/users/${userId}/boosters`,
    {
      params: {
        limit,
        offset,
      },
    }
  );
  return response.data;
};

// Function to fetch a single booster by ID
export const getBoosterById = async (id: string): Promise<boosterData> => {
  const response = await api.get(`${API_URL}/${id}`);
  return response.data;
};

// Function to update an existing booster
export const updateBooster = async (
  id: string,
  booster: Partial<boosterData>
): Promise<boosterData> => {
  const response = await api.put(`${API_URL}/${id}`, booster);
  return response.data;
};

// Function to delete a booster by ID
export const deleteBooster = async (id: number): Promise<boolean> => {
  const response = await api.delete(`${API_URL}/${id}`);
  return response.status === 200;
};

export const getCardsForBooster = async (
  boosterId: string
): Promise<BoosterCards[]> => {
  const response = await api.get(`${API_URL}/${boosterId}/cards`);
  return response.data; // Returning the list of cards
};

export const deleteCardForBooster = async (
  boosterId: string,
  cardId: number
): Promise<boolean> => {
  // Replace with your actual API endpoint for deleting a booster-card relationship
  const response = await api.delete(`${API_URL}/${boosterId}/card/${cardId}`);
  return response.status === 200; // Returns true if the deletion was successful
};

export const createBoosterCards = async (
  boosterId: string,
  cardsId: number[]
): Promise<boolean> => {
  const response = await api.post(`${API_URL}/${boosterId}/cards`, {
    cardsId: cardsId,
  });
  return response.status === 201;
};

export const getRandomCardsForBooster = async (
  boosterId: string
): Promise<CardData[]> => {
  const response = await api.get(`${API_URL}/${boosterId}/random/cards`);
  return response.data; // Returning the list of cards
};

export const generateBooster = async (
  boosterData: Omit<
    GenerateBoosterData,
    | "id"
    | "created_at"
    | "updated_at"
    | "image_id"
    | "original_image_id"
    | "price"
    | "is_in_store"
    | "masterset_card_id"
    | "status"
  >
): Promise<any> => {
  const response = await api.post(`${API_URL}/generate/`, boosterData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const deleteBoosters = async (
  ids: string[]
): Promise<deleteBoostersResult> => {
  const response = await api.delete(API_URL, {
    data: { ids },
  });

  return response.data;
};

export const archiveBoosters = async (ids: string[]): Promise<boolean> => {
  const response = await api.put(`${API_URL}/archive`, { ids: ids });
  return response.status === 200;
};

export const getCreatorArchivedBoosters = async (
  limit: number,
  offset: number
): Promise<boosterData[]> => {
  const response = await api.get(`${API_URL}/archived`, {
    params: {
      limit,
      offset,
    },
  });
  return response.data;
};

export const getMaxPurchasableQuantity = async (
  boosterId: string
): Promise<number> => {
  const response = await api.get(`${API_URL}/${boosterId}/max-purchasable`);
  return response.data;
};
